import { copyText } from "@/lib/uid.ts";
import type { DevicePreset } from "@codvero/shared";
import { formatElementCapture } from "@codvero/shared";
import { AppWindow, ArrowLeft, ArrowRight, Bot, Crosshair, Globe, Hand, Loader2, Monitor, Plus, RotateCw, Smartphone, Tablet, TerminalSquare, X } from "lucide-react";
import { AppsMenu } from "./AppsMenu.tsx";
import { useCallback, useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent, type PointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn.ts";
import { Alert, Button, Spinner } from "@/ui/primitives.tsx";
import { useBrowserSession } from "./useBrowserSession.ts";
import { viewportFor } from "./viewport.ts";
import { browserOpenBus, takePendingBrowserOpen } from "./openBus.ts";

const MOD = { alt: 1, ctrl: 2, meta: 4, shift: 8 };
const modifiersOf = (e: { altKey: boolean; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }) => (e.altKey ? MOD.alt : 0) | (e.ctrlKey ? MOD.ctrl : 0) | (e.metaKey ? MOD.meta : 0) | (e.shiftKey ? MOD.shift : 0);
const KEYCODES: Record<string, number> = { Enter: 13, Tab: 9, Escape: 27, Backspace: 8, Delete: 46, ArrowUp: 38, ArrowDown: 40, ArrowLeft: 37, ArrowRight: 39, Home: 36, End: 35, PageUp: 33, PageDown: 34, " ": 32, Shift: 16, Control: 17, Alt: 18, Meta: 91 };

/** Browser pane: tabs, address bar, live screencast on a canvas, input forwarding, picker and dev panels. */
export function BrowserPane({ projectId, canEdit, onCapture, className }: { projectId: string; canEdit: boolean; onCapture?: (block: string) => void; className?: string }) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const frameSize = useRef({ w: 1280, h: 800 });

  // The server only streams the tab this viewer is attached to, so every frame is ours to draw.
  const onFrame = useCallback((_tabId: string, blob: Blob) => {
    void createImageBitmap(blob).then((bmp) => {
      const c = canvasRef.current;
      if (!c) return;
      if (c.width !== bmp.width || c.height !== bmp.height) {
        c.width = bmp.width;
        c.height = bmp.height;
      }
      frameSize.current = { w: bmp.width, h: bmp.height };
      c.getContext("2d")?.drawImage(bmp, 0, 0);
      bmp.close();
    });
  }, []);

  const b = useBrowserSession(projectId, true, onFrame);
  const [address, setAddress] = useState("");
  const [editing, setEditing] = useState(false);
  const [device, setDevice] = useState<DevicePreset>("desktop");
  const [picker, setPicker] = useState(false);
  const [panel, setPanel] = useState<"none" | "console" | "network">("none");
  const [captured, setCaptured] = useState<string | null>(null);
  const human = b.state?.humanControl ?? false;

  useEffect(() => {
    if (!editing) setAddress(b.tab?.url && b.tab.url !== "about:blank" ? b.tab.url : "");
  }, [b.tab?.url, editing]);

  // Open URLs requested from elsewhere (e.g. a dev server the agent just started) once the session is connected.
  const sendRef = useRef(b.send);
  sendRef.current = b.send;
  const stateRef = useRef(b.state);
  stateRef.current = b.state; // the listener below lives across renders: read the current tabs, not the first ones
  useEffect(() => {
    if (!b.connected || !b.state) return;
    const open = (url: string) => {
      const st = stateRef.current;
      const current = st?.tabs.find((tb) => tb.id === st?.activeTabId);
      if (current && (!current.url || current.url === "about:blank")) sendRef.current({ type: "navigate", url });
      else sendRef.current({ type: "tabs.create", url });
    };
    const queued = takePendingBrowserOpen();
    if (queued) open(queued);
    const h = (e: Event) => { takePendingBrowserOpen(); open((e as CustomEvent<string>).detail); };
    browserOpenBus.addEventListener("open", h);
    return () => browserOpenBus.removeEventListener("open", h);
  }, [b.connected, !!b.state]);

  // The page's viewport, and how it is shown in the pane: a computer is at least 1280 wide (a narrow pane shows the
  // whole desktop page scaled down, landscape, not the site's phone layout); a tablet or a phone keeps its size and
  // is scaled to fit. The image is never stretched: one scale for both sides, centred.
  const [host, setHost] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      setHost((cur) => (cur.w === Math.round(r.width) && cur.h === Math.round(r.height) ? cur : { w: Math.round(r.width), h: Math.round(r.height) }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const view = viewportFor(device, host);
  const fit = host.w && host.h ? Math.min(1, host.w / view.w, host.h / view.h) : 1;
  // the stream's resolution follows what is displayed (a scaled-down page needs no full-size frames)
  const frameScale = Math.min(2, Math.max(0.5, (window.devicePixelRatio || 1) * fit));
  useEffect(() => {
    if (!host.w || !host.h) return;
    b.resize(view.w, view.h, Math.round(frameScale * 100) / 100);
  }, [b.resize, b.connected, view.w, view.h, Math.round(frameScale * 100)]);

  useEffect(() => {
    if (b.lastCapture) {
      const block = formatElementCapture(b.lastCapture);
      setCaptured(block);
      setPicker(false);
      onCapture?.(block);
      b.clearCapture();
    }
  }, [b.lastCapture]);

  /** Map a pointer position on the displayed canvas to CSS pixels of the page viewport. */
  const toPage = (e: { clientX: number; clientY: number }) => {
    const c = canvasRef.current!;
    const r = c.getBoundingClientRect();
    const sx = view.w / r.width;
    const sy = view.h / r.height;
    return { x: (e.clientX - r.left) * sx, y: (e.clientY - r.top) * sy };
  };
  const interactive = canEdit && (human || picker);
  const button = (e: PointerEvent): "left" | "right" | "middle" => (e.button === 2 ? "right" : e.button === 1 ? "middle" : "left");
  const onPointer = (event: "move" | "down" | "up") => (e: PointerEvent<HTMLCanvasElement>) => {
    if (!interactive) return;
    const p = toPage(e);
    if (event === "down") (e.target as HTMLElement).focus();
    b.send({ type: "mouse", event, x: p.x, y: p.y, button: event === "move" && e.buttons === 0 ? "none" : button(e), clickCount: event === "move" ? 0 : e.detail || 1, deltaX: 0, deltaY: 0, modifiers: modifiersOf(e) });
  };
  const wheelRef = useRef<(e: globalThis.WheelEvent) => void>(() => {});
  wheelRef.current = (e) => {
    if (!interactive || picker) return;
    e.preventDefault();
    const p = toPage(e);
    b.send({ type: "mouse", event: "wheel", x: p.x, y: p.y, button: "none", clickCount: 0, deltaX: e.deltaX, deltaY: e.deltaY, modifiers: modifiersOf(e) });
  };
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const h = (e: globalThis.WheelEvent) => wheelRef.current(e);
    c.addEventListener("wheel", h, { passive: false });
    return () => c.removeEventListener("wheel", h);
  }, []);
  const onKey = (event: "down" | "up") => (e: KeyboardEvent<HTMLCanvasElement>) => {
    if (!human || picker) return;
    if (e.key === "Escape" && event === "down" && !e.ctrlKey && !e.metaKey) {
      // Escape is reserved: hand control back.
      b.send({ type: "control", human: false });
      return;
    }
    // Ctrl/Cmd+V must reach the browser as a native paste event (onPaste below), not as a key press to the remote page.
    if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === "v") return;
    e.preventDefault();
    const printable = e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
    b.send({ type: "key", event, key: e.key, code: e.code, keyCode: KEYCODES[e.key] ?? (e.key.length === 1 ? e.key.toUpperCase().charCodeAt(0) : 0), ...(printable ? { text: e.key } : {}), modifiers: modifiersOf(e) });
  };
  const onPaste = (e: ClipboardEvent<HTMLCanvasElement>) => {
    if (!human || picker) return;
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    if (text) b.send({ type: "text", text });
  };
  const go = (e?: { preventDefault(): void }) => {
    e?.preventDefault();
    if (address.trim()) b.send({ type: "navigate", url: address.trim() });
    setEditing(false);
    canvasRef.current?.focus();
  };

  const status = b.state?.status ?? "stopped";
  const tabs = b.state?.tabs ?? [];
  const [appsOpen, setAppsOpen] = useState(false);

  return (
    <div className={cn("flex h-full min-h-[480px] flex-col overflow-hidden rounded-lg border border-border bg-surface", className)} dir="ltr">
      {/* tab strip */}
      <div className="flex items-center gap-1 overflow-x-auto border-b border-border bg-surface-2 px-2 pt-2">
        {tabs.map((tab) => (
          <div key={tab.id} className={cn("group flex max-w-[200px] shrink-0 items-center gap-1 rounded-t-md border border-b-0 px-2 py-1 text-xs", tab.id === b.viewTabId ? "border-border bg-surface" : "border-transparent text-fg-muted hover:bg-surface/60")}>
            <button type="button" className="flex min-w-0 items-center gap-1" onClick={() => b.selectTab(tab.id)} title={tab.url}>
              {tab.agentActive && <Bot className="size-3 shrink-0 text-accent" />}
              <span className="truncate">{tab.title || tab.url || t("browser.newTab")}</span>
            </button>
            {canEdit && tabs.length > 1 && (
              <button type="button" aria-label={t("common.close")} className={cn("rounded p-0.5 hover:bg-border [@media(hover:none)]:visible [@media(hover:none)]:p-1", tab.id === b.viewTabId ? "visible" : "invisible group-hover:visible")} onClick={() => b.send({ type: "tabs.close", tabId: tab.id })}>
                <X className="size-3" />
              </button>
            )}
          </div>
        ))}
        {canEdit && (
          <button type="button" aria-label={t("browser.newTab")} className="rounded p-1 text-fg-muted hover:bg-surface" onClick={() => b.send({ type: "tabs.create" })}>
            <Plus className="size-4" />
          </button>
        )}
        <div className="ms-auto flex items-center gap-1 pb-1 text-xs text-fg-muted">
          {b.state?.version && <span className="hidden sm:inline">{b.state.version}</span>}
          <span className={cn("size-2 rounded-full", status === "ready" && b.connected ? "bg-success" : status === "error" ? "bg-danger" : "bg-warn animate-pulse")} />
        </div>
      </div>

      {/* address bar */}
      {/* phones: the address takes its own row, the buttons wrap underneath */}
      {/* not a <form>: the apps menu inside it has its own publish form, and a form nested in a form made the
          browser submit this outer one natively (full page reload, nothing published) */}
      <div role="toolbar" className="relative flex flex-wrap items-center gap-1 border-b border-border px-2 py-1.5 sm:flex-nowrap">
        <Button type="button" size="sm" variant="ghost" disabled={!canEdit || !b.tab?.canGoBack} onClick={() => b.send({ type: "history", direction: "back" })} aria-label={t("common.back")}><ArrowLeft className="size-4" /></Button>
        <Button type="button" size="sm" variant="ghost" disabled={!canEdit || !b.tab?.canGoForward} onClick={() => b.send({ type: "history", direction: "forward" })} aria-label="forward"><ArrowRight className="size-4" /></Button>
        <Button type="button" size="sm" variant="ghost" disabled={!canEdit} onClick={() => b.send({ type: b.tab?.loading ? "stop_loading" : "reload" })} aria-label={t("browser.reload")}>
          {b.tab?.loading ? <X className="size-4" /> : <RotateCw className="size-4" />}
        </Button>
        <input
          className="order-first h-9 min-w-0 flex-1 basis-full rounded-md border border-border bg-bg px-3 font-mono text-xs focus:border-accent focus:outline-none sm:order-none sm:h-8 sm:basis-0"
          value={address}
          placeholder={t("browser.addressPlaceholder")}
          disabled={!canEdit}
          onFocus={(e) => { setEditing(true); e.target.select(); }}
          onBlur={() => setEditing(false)}
          onChange={(e) => setAddress(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) go(e); }}
          enterKeyHint="go"
        />
        <div className="hidden items-center gap-0.5 sm:flex">
          {(["desktop", "tablet", "mobile"] as DevicePreset[]).map((p) => {
            const Icon = p === "desktop" ? Monitor : p === "tablet" ? Tablet : Smartphone;
            return <Button key={p} type="button" size="sm" variant={device === p ? "secondary" : "ghost"} disabled={!canEdit} aria-label={p} onClick={() => { setDevice(p); b.send({ type: "device", preset: p }); }}><Icon className="size-4" /></Button>;
          })}
        </div>
        {/* the menu anchors to the toolbar (relative above), not this button, so it never spills out of the panel */}
        <div>
          <Button type="button" size="sm" variant={appsOpen ? "secondary" : "ghost"} aria-label={t("browser.apps")} title={t("browser.apps")} onClick={() => setAppsOpen(!appsOpen)}><Globe className="size-4" /></Button>
          {appsOpen && <AppsMenu projectId={projectId} canEdit={canEdit} onOpenInternal={(url) => b.send({ type: "tabs.create", url })} onClose={() => setAppsOpen(false)} />}
        </div>
        <Button type="button" size="sm" variant={picker ? "secondary" : "ghost"} disabled={!canEdit} aria-label={t("browser.pick")} title={t("browser.pick")} onClick={() => { const n = !picker; setPicker(n); b.send({ type: "picker", enabled: n }); }}><Crosshair className="size-4" /></Button>
        <Button type="button" size="sm" variant={panel !== "none" ? "secondary" : "ghost"} aria-label="devtools" onClick={() => setPanel(panel === "none" ? "console" : "none")}><TerminalSquare className="size-4" /></Button>
        {canEdit && <Button type="button" size="sm" variant={b.state?.headed ? "secondary" : "ghost"} aria-label={t("browser.headed")} title={b.state?.headed ? t("browser.headedOn") : t("browser.headedOff")} onClick={() => b.send({ type: "mode", headed: !b.state?.headed })}><AppWindow className="size-4" /></Button>}
        {canEdit && (
          <Button type="button" size="sm" variant={human ? "primary" : "secondary"} onClick={() => b.send({ type: "control", human: !human })}>
            {human ? <Hand className="size-4" /> : <Bot className="size-4" />} <span className="hidden sm:inline">{human ? t("browser.youControl") : t("browser.takeControl")}</span>
          </Button>
        )}
      </div>

      {/* viewport */}
      <div ref={hostRef} className="relative grid min-h-0 flex-1 place-items-center overflow-hidden bg-[#202124]">
        {status !== "ready" && (
          <div className="absolute inset-0 z-10 grid place-items-center bg-bg/80 text-sm text-fg-muted">
            {status === "starting" ? <div className="flex items-center gap-2"><Loader2 className="size-4 animate-spin" /> {t("browser.starting")}</div> : status === "error" ? <Alert tone="danger">{b.state?.error}</Alert> : <Spinner />}
          </div>
        )}
        {b.error && <div className="absolute start-2 top-2 z-10"><Alert tone="danger">{b.error}</Alert></div>}
        <canvas
          ref={canvasRef}
          tabIndex={0}
          className={cn("block outline-none", (device !== "desktop" || fit < 1) && "shadow-lg", picker ? "cursor-crosshair" : human ? "cursor-default" : "cursor-not-allowed", human && "ring-2 ring-inset ring-accent/70")}
          style={{ width: Math.round(view.w * fit), height: Math.round(view.h * fit) }}
          onPointerMove={onPointer("move")}
          onPointerDown={onPointer("down")}
          onPointerUp={onPointer("up")}
          onKeyDown={onKey("down")}
          onKeyUp={onKey("up")}
          onPaste={onPaste}
          onContextMenu={(e) => e.preventDefault()}
        />
        {!human && !picker && canEdit && status === "ready" && (
          <button type="button" onClick={() => b.send({ type: "control", human: true })} className="absolute bottom-3 left-1/2 z-10 -translate-x-1/2 rounded-full bg-surface/90 px-3 py-1 text-xs shadow backdrop-blur hover:bg-surface">
            {t("browser.watching")}
          </button>
        )}
        {human && <div className="pointer-events-none absolute bottom-3 left-1/2 z-10 -translate-x-1/2 rounded-full bg-accent px-3 py-1 text-xs text-accent-fg shadow">{t("browser.escToRelease")}</div>}
      </div>

      {captured && (
        <div className="flex items-center justify-between gap-2 border-t border-border bg-success-soft px-3 py-1.5 text-xs">
          <span>{onCapture ? t("browser.captured") : t("browser.capturedCopy")}</span>
          <div className="flex gap-1">
            {!onCapture && <Button size="sm" variant="secondary" onClick={() => { void copyText(captured); }}>{t("common.copy")}</Button>}
            <Button size="sm" variant="ghost" onClick={() => setCaptured(null)}><X className="size-3" /></Button>
          </div>
        </div>
      )}

      {panel !== "none" && (
        <div className="flex h-44 flex-col border-t border-border bg-bg text-xs">
          <div className="flex items-center gap-1 border-b border-border px-2 py-1">
            <button type="button" className={cn("rounded px-2 py-0.5", panel === "console" && "bg-surface-2")} onClick={() => setPanel("console")}>Console ({b.console.length})</button>
            <button type="button" className={cn("rounded px-2 py-0.5", panel === "network" && "bg-surface-2")} onClick={() => setPanel("network")}>Network ({b.network.length})</button>
            <button type="button" className="ms-auto rounded px-2 py-0.5 text-fg-muted hover:text-fg" onClick={b.clearLogs}>{t("browser.clear")}</button>
            <button type="button" className="rounded p-0.5" onClick={() => setPanel("none")}><X className="size-3" /></button>
          </div>
          <div className="min-h-0 flex-1 overflow-auto p-2 font-mono">
            {panel === "console"
              ? b.console.filter((e) => e.tabId === b.viewTabId).map((e, i) => <div key={i} className={cn("whitespace-pre-wrap break-all", e.level === "error" ? "text-danger" : e.level === "warning" ? "text-warn" : "")}>{e.text}</div>)
              : b.network.filter((e) => e.tabId === b.viewTabId).map((e) => <div key={e.id} className="flex gap-2 truncate"><span className={cn("w-8 shrink-0", e.status && e.status >= 400 ? "text-danger" : "text-fg-muted")}>{e.status ?? "…"}</span><span className="w-10 shrink-0 text-fg-muted">{e.method}</span><span className="truncate">{e.url}</span></div>)}
          </div>
        </div>
      )}
    </div>
  );
}
